import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { aiService } from '../services/aiService';
import { motion } from 'framer-motion';
import { BeakerIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { RootState } from '../store/store';

interface AIQuizGeneratorProps {
  onSuccess?: (collectionId: string) => void;
}

export const AIQuizGenerator: React.FC<AIQuizGeneratorProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [quotaInfo, setQuotaInfo] = useState<{remainingQuizzes: number, aiQuizGenerated: number} | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await aiService.generateQuiz({ topic, level });
      if (response.remainingQuizzes !== undefined) {
        setQuotaInfo({
          remainingQuizzes: response.remainingQuizzes,
          aiQuizGenerated: response.aiQuizGenerated
        });
      }
      if (onSuccess) {
        onSuccess(response.collection._id);
      } else {
        navigate(`/collections/${response.collection._id}`);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError(err.response.data.message);
        setQuotaInfo({
          remainingQuizzes: err.response.data.remainingQuizzes,
          aiQuizGenerated: err.response.data.aiQuizGenerated
        });
      } else {
        setError('Failed to generate quiz. Please try again.');
      }
      console.error('Error generating quiz:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100/50 dark:border-gray-700/50"
    >
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
        Generate AI Quiz
      </h2>

      {!user?.isPremium && (
        <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
          <div className="flex items-center mb-2">
            <BeakerIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
            <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              Free User Quota
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {quotaInfo
              ? `${quotaInfo.remainingQuizzes} AI quizzes remaining today`
              : 'You can generate up to 2 AI quizzes per day'}
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center"
          >
            <SparklesIcon className="h-4 w-4 mr-1" />
            Upgrade for unlimited AI quizzes
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="group">
          <label htmlFor="topic" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400">
            Quiz Topic
          </label>
          <div className="relative">
            <input
              type="text"
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="block w-full px-4 py-3 rounded-lg border-2 bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600 
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 dark:text-white 
                placeholder-gray-400 transition-all duration-200 ease-in-out
                hover:border-gray-300 dark:hover:border-gray-500"
              placeholder="e.g. JavaScript Fundamentals"
              required
            />
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 opacity-0 group-focus-within:opacity-10 transition-opacity pointer-events-none"></div>
          </div>
        </div>

        <div className="group">
          <label htmlFor="level" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400">
            Difficulty Level
          </label>
          <div className="relative">
            <select
              id="level"
              value={level}
              onChange={(e) => setLevel(e.target.value as any)}
              className="block w-full px-4 py-3 rounded-lg border-2 bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600 
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 dark:text-white 
                transition-all duration-200 ease-in-out appearance-none
                hover:border-gray-300 dark:hover:border-gray-500"
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 opacity-0 group-focus-within:opacity-10 transition-opacity pointer-events-none"></div>
          </div>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-600 dark:text-red-400 text-sm text-center"
          >
            {error}
          </motion.div>
        )}

        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
            ${isLoading 
              ? 'bg-indigo-400 dark:bg-indigo-500/50 cursor-not-allowed' 
              : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'}`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Generating Quiz...
            </>
          ) : (
            'Generate Quiz'
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};